import React, { useEffect, useRef } from 'react';

import { ERROR_TYPE } from 'src/store/error/constants';
import { PAY_ORDER, PAYMENT_ERRORS } from 'src/store/payment/constants';
import cn from 'classnames';
import type {
  CardUniqueInfo,
  PaymentFailPayload,
} from 'src/store/payment/types';
import styles from './payment3dsPopup.module.css';
import { PAYMENT_REDIRECT_IFRAME_3DS_DATA } from '../../constants/payment';
import Popup from '../Popup';

const iframeName = 'paymentIframe';

export const popupClassNames = {
  modal: styles.popupDesktop,
  overlay: styles.overlayDesktop,
};

export const popupClassNamesMobile = {
  modal: styles.popupMobile,
  overlay: styles.overlayMobile,
};

type Props = {
  isMobile: boolean;
  submitPares: (paRes: string) => void;
  submitCres: (cres: string) => void;
  closePopup: () => void;
  onFail: (value: PaymentFailPayload) => void;
  data?: CardUniqueInfo['data3ds'];
  isOpened: boolean;
};

const Payment3dsPopup = (props: Props) => {
  const {
    data: threeDSData,
    isOpened,
    closePopup,
    submitPares,
    submitCres,
    isMobile,
    onFail,
  } = props;

  const formRef = useRef<HTMLFormElement>(null);

  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const timeoutRef = useRef<number | null>(null);

  useEffect(() => {
    if (isOpened) {
      formRef.current?.submit();
      timeoutRef.current = window.setTimeout(
        () =>
          onFail({
            type: ERROR_TYPE.ORDER_3DS,
            action: PAY_ORDER,
            message: PAYMENT_ERRORS.THREE_DS,
          }),
        10000,
      );
    }
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpened]);

  const onLoad = (event) => {
    try {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      const { PaRes, cres } =
        // @ts-ignore
        event.target.contentWindow[PAYMENT_REDIRECT_IFRAME_3DS_DATA];
      if (PaRes) submitPares(PaRes);
      if (cres) submitCres(cres);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Iframe 3ds', error);
    }
  };

  if (
    !threeDSData ||
    ('processorIdentifier' in threeDSData &&
      threeDSData.processorIdentifier !== 'uniteller_v1') ||
    'required3DS' in threeDSData
  )
    return null;

  return (
    <Popup
      classNames={isMobile ? popupClassNamesMobile : popupClassNames}
      open={isOpened}
      closeOnEsc={false}
      closeOnOverlayClick={false}
      showCloseIcon={false}
      onClose={closePopup}
    >
      <form
        target={iframeName}
        action={threeDSData.url}
        method="post"
        ref={formRef}
      >
        {'pareq' in threeDSData ? (
          <>
            <input type="hidden" name="PaReq" value={threeDSData.pareq} />
            <input type="hidden" name="MD" value={threeDSData.md} />
            <input type="hidden" name="TermUrl" value={threeDSData.termURL} />
          </>
        ) : (
          <>
            <input type="hidden" name="creq" value={threeDSData.creq} />
            {threeDSData.threeDsSessionData && (
              <input
                type="hidden"
                name="threeDSSessionData"
                value={threeDSData.threeDsSessionData}
              />
            )}
          </>
        )}
      </form>
      <iframe
        ref={iframeRef}
        title="3ds payment"
        name={iframeName}
        onLoad={onLoad}
        className={cn(styles.iframeDesktop, {
          [styles.iframeMobile]: isMobile,
        })}
      />
    </Popup>
  );
};

export default Payment3dsPopup;
